import { getModelById, getModelDisplayName } from "@mycompbox/shared";
import { AlertTriangle, Cpu } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Epic } from "@/lib/api/types";

interface ModelRecommendationCardProps {
  epic: Epic;
}

function ThinkingBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    none: "bg-zinc-700 text-zinc-300",
    low: "bg-blue-900/50 text-blue-300",
    medium: "bg-yellow-900/50 text-yellow-300",
    high: "bg-red-900/50 text-red-300",
  };
  const colorClass = colors[level] ?? colors.none;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
      {level}
    </span>
  );
}

function TierBadge({ tier }: { tier: number }) {
  const colors: Record<number, string> = {
    1: "bg-emerald-900/50 text-emerald-300",
    2: "bg-amber-900/50 text-amber-300",
    3: "bg-purple-900/50 text-purple-300",
  };
  const colorClass = colors[tier] ?? colors[1];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
      Tier {tier}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ModelRecommendationCard({ epic }: ModelRecommendationCardProps) {
  const hasRecommendation = !!epic.recommendedModel;
  const hasActual = !!epic.actualModelUsed;

  if (!hasRecommendation && !hasActual) return null;

  const recommendedEntry = epic.recommendedModel
    ? getModelById(epic.recommendedModel)
    : undefined;
  const actualEntry = epic.actualModelUsed
    ? getModelById(epic.actualModelUsed)
    : undefined;

  const hasModelMismatch =
    hasRecommendation &&
    hasActual &&
    epic.recommendedModel !== epic.actualModelUsed;

  const hasDeviationJustification = !!epic.modelDeviationJustification;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
        <Cpu className="h-4 w-4 text-zinc-400" />
        AI Model Recommendation
      </div>

      {/* Recommended section */}
      {hasRecommendation && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Recommended
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-200">
              {getModelDisplayName(epic.recommendedModel!) ?? epic.recommendedModel}
            </span>
            {recommendedEntry && <TierBadge tier={recommendedEntry.tier} />}
            {epic.recommendedThinkingLevel && (
              <ThinkingBadge level={epic.recommendedThinkingLevel} />
            )}
          </div>
          {epic.modelRecommendationReason && (
            <p className="text-xs text-zinc-400 italic">
              {epic.modelRecommendationReason}
            </p>
          )}
          {(epic.modelRecommendationSource || epic.modelRecommendationUpdatedAt) && (
            <p className="text-xs text-zinc-500">
              {epic.modelRecommendationSource && (
                <span>Source: {epic.modelRecommendationSource}</span>
              )}
              {epic.modelRecommendationSource && epic.modelRecommendationUpdatedAt && " · "}
              {epic.modelRecommendationUpdatedAt && (
                <span>{formatDate(epic.modelRecommendationUpdatedAt)}</span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Actual section — amber border when mismatch */}
      {hasActual && (
        <div className={cn(
          "rounded-lg border p-3 space-y-2",
          hasModelMismatch
            ? "border-amber-500/40 bg-amber-950/20"
            : "border-white/[0.06] bg-white/[0.02]"
        )}>
          <div className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Model Used
            {hasModelMismatch && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                Deviation
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-200">
              {getModelDisplayName(epic.actualModelUsed!) ?? epic.actualModelUsed}
            </span>
            {actualEntry && <TierBadge tier={actualEntry.tier} />}
            {epic.actualThinkingLevelUsed && (
              <ThinkingBadge level={epic.actualThinkingLevelUsed} />
            )}
          </div>
          {epic.actualModelRecordedAt && (
            <p className="text-xs text-zinc-500">
              Recorded {formatDate(epic.actualModelRecordedAt)}
            </p>
          )}
        </div>
      )}

      {/* Deviation justification panel */}
      {hasModelMismatch && hasDeviationJustification && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-950/10 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-400 uppercase tracking-wide">
            <AlertTriangle className="h-3 w-3" />
            Deviation Justification
          </div>
          <p className="text-xs text-zinc-300">
            {epic.modelDeviationJustification}
          </p>
        </div>
      )}
    </div>
  );
}
